import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Shield, Trash2, CheckCircle, XCircle, Mail, Eye, Globe, Calendar, Store, Users, DollarSign, Settings, UserPlus, AlertTriangle, Database, CreditCard, ToggleLeft, ToggleRight, TestTube, Video, Image, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const AdminSettingsTab = ({ user, fetchDashboardData }) => {
  const { t } = useTranslation();

  // Admin Management State
  const [admins, setAdmins] = useState([]);
  const [dbStats, setDbStats] = useState(null);
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [showResetDbModal, setShowResetDbModal] = useState(false);
  const [newAdminForm, setNewAdminForm] = useState({ email: '', password: '', name: '' });
  const [resetDbForm, setResetDbForm] = useState({ confirmation_password: '', reset_key: '', keep_admin: true });

  // PayPal Settings State
  const [paypalSettings, setPaypalSettings] = useState(null);
  const [paypalForm, setPaypalForm] = useState({ mode: 'sandbox', client_id: '', secret: '' });
  const [paypalTestResult, setPaypalTestResult] = useState(null);
  const [paypalSaving, setPaypalSaving] = useState(false);

  // Exchange Rate State
  const [exchangeRate, setExchangeRate] = useState({ eur_rate: 0.92, updated_at: null });
  const [exchangeRateSaving, setExchangeRateSaving] = useState(false);

  // Site Settings State
  const [siteSettingsForm, setSiteSettingsForm] = useState({ 
    intro_video_url: '', intro_video_url_fr: '', intro_video_url_es: '', intro_video_url_de: '', intro_video_poster: '' 
  });
  const [siteSettingsSaving, setSiteSettingsSaving] = useState(false);

  // Sitemap State
  const [sitemapStatus, setSitemapStatus] = useState(null);
  const [sitemapRegenerating, setSitemapRegenerating] = useState(false);

  // File Health Check State
  const [fileHealthCheck, setFileHealthCheck] = useState(null);
  const [fileHealthLoading, setFileHealthLoading] = useState(false);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [adminsRes, dbStatsRes, paypalRes, exchangeRes, siteRes, sitemapRes] = await Promise.all([
        axios.get(`${API}/admin/list`),
        axios.get(`${API}/admin/database/stats`),
        axios.get(`${API}/admin/settings/paypal`),
        axios.get(`${API}/settings/exchange-rate`),
        axios.get(`${API}/settings`).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/sitemap-status`).catch(() => ({ data: null })),
      ]);
      setAdmins(adminsRes.data);
      setDbStats(dbStatsRes.data);
      setPaypalSettings(paypalRes.data);
      setPaypalForm({
        mode: paypalRes.data.mode,
        client_id: paypalRes.data.client_id_full || '',
        secret: ''
      });
      setExchangeRate({ eur_rate: exchangeRes.data.eur_rate || 0.92, updated_at: exchangeRes.data.updated_at });
      setSiteSettingsForm({
        intro_video_url: siteRes.data.intro_video_url || '',
        intro_video_url_fr: siteRes.data.intro_video_url_fr || '',
        intro_video_url_es: siteRes.data.intro_video_url_es || '',
        intro_video_url_de: siteRes.data.intro_video_url_de || '',
        intro_video_poster: siteRes.data.intro_video_poster || ''
      });
      setSitemapStatus(sitemapRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data', error);
    }
  };

  const regenerateSitemap = async () => {
    setSitemapRegenerating(true);
    try {
      const response = await axios.post(`${API}/admin/regenerate-sitemap`);
      toast.success(`Sitemap regenerated! ${response.data.stats.total_urls} URLs indexed.`);
      setSitemapStatus({
        ...sitemapStatus,
        last_modified: response.data.regenerated_at,
        sitemap_exists: true,
        current_counts: {
          events: response.data.stats.events,
          exhibitors: response.data.stats.exhibitors
        }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate sitemap');
    } finally {
      setSitemapRegenerating(false);
    }
  };

  const updateSiteSettings = async (e) => {
    e.preventDefault();
    setSiteSettingsSaving(true);
    try {
      await axios.post(`${API}/admin/settings/site`, siteSettingsForm);
      toast.success('Site settings updated successfully');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update site settings');
    } finally {
      setSiteSettingsSaving(false);
    }
  };

  const savePaypalSettings = async (e) => {
    e.preventDefault();
    setPaypalSaving(true);
    setPaypalTestResult(null);
    try {
      await axios.post(`${API}/admin/settings/paypal`, paypalForm);
      toast.success(`PayPal settings updated to ${paypalForm.mode.toUpperCase()} mode`);
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update PayPal settings');
    } finally {
      setPaypalSaving(false);
    }
  };

  const testPaypalConnection = async () => {
    setPaypalTestResult(null);
    try {
      const response = await axios.post(`${API}/admin/settings/paypal/test`);
      setPaypalTestResult(response.data);
      if (response.data.success) {
        toast.success('PayPal connection test successful!');
      } else {
        toast.error('PayPal connection test failed');
      }
    } catch (error) {
      setPaypalTestResult({ success: false, message: error.response?.data?.detail || 'Test failed' });
      toast.error('PayPal connection test failed');
    }
  };

  const createAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/create-admin`, newAdminForm);
      toast.success('Admin account created successfully');
      setShowCreateAdminModal(false);
      setNewAdminForm({ email: '', password: '', name: '' });
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    }
  };

  const removeAdmin = async (adminId) => {
    if (!window.confirm('Are you sure you want to remove this admin? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/remove-admin/${adminId}`);
      toast.success('Admin removed successfully');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const checkFileHealth = async () => {
    setFileHealthLoading(true);
    try {
      const response = await axios.get(`${API}/admin/files/check`);
      setFileHealthCheck(response.data);
      if (response.data.status === 'ok') {
        toast.success('All files are present!');
      } else {
        toast.warning(`Found ${response.data.total_missing} missing files`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check file health');
    } finally {
      setFileHealthLoading(false);
    }
  };

  const cleanupTestData = async () => {
    if (!window.confirm('This will delete all booths with invalid URLs (example.com or missing files). Continue?')) return;
    try {
      const response = await axios.post(`${API}/admin/files/cleanup-test-data`);
      toast.success(response.data.message);
      checkFileHealth();
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cleanup test data');
    }
  };

  const resetDatabase = async (e) => {
    e.preventDefault();
    if (!window.confirm('\u26a0\ufe0f FINAL WARNING: This will permanently delete all data. Are you absolutely sure?')) return;
    try {
      await axios.post(`${API}/admin/database/reset`, resetDbForm);
      toast.success('Database reset completed');
      setShowResetDbModal(false);
      setResetDbForm({ confirmation_password: '', reset_key: '', keep_admin: true });
      fetchDashboardData();
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset database');
    }
  };

  return (
    <>
      <div className="space-y-6">
        {/* Admin Management Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Admin Management
            </CardTitle>
            <CardDescription>
              Manage administrator accounts. Only existing admins can create or remove other admins.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-slate-600">
                {admins.length} admin account{admins.length !== 1 ? 's' : ''} configured
              </p>
              <Button onClick={() => setShowCreateAdminModal(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Add Admin
              </Button>
            </div>
            
            {admins.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.map((admin) => (
                    <TableRow key={admin.id}>
                      <TableCell className="font-medium">{admin.name}</TableCell>
                      <TableCell>{admin.email}</TableCell>
                      <TableCell>{new Date(admin.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {admin.id === user?.id ? (
                          <Badge variant="outline">Current User</Badge>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-600 hover:text-red-700"
                            onClick={() => removeAdmin(admin.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* PayPal Settings Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              PayPal Settings
            </CardTitle>
            <CardDescription>
              Configure PayPal payment integration. Switch between Sandbox (testing) and Live (production) modes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {paypalSettings && (
              <div className={`p-4 rounded-lg mb-6 ${paypalSettings.is_live ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {paypalSettings.is_live ? (
                      <ToggleRight className="h-8 w-8 text-green-600" />
                    ) : (
                      <ToggleLeft className="h-8 w-8 text-amber-600" />
                    )}
                    <div>
                      <p className={`font-bold ${paypalSettings.is_live ? 'text-green-800' : 'text-amber-800'}`}>
                        {paypalSettings.is_live ? 'LIVE MODE' : 'SANDBOX MODE'}
                      </p>
                      <p className={`text-sm ${paypalSettings.is_live ? 'text-green-600' : 'text-amber-600'}`}>
                        {paypalSettings.is_live 
                          ? 'Real payments are being processed' 
                          : 'Test mode - no real money is transferred'}
                      </p>
                    </div>
                  </div>
                  <Badge className={paypalSettings.has_credentials ? 'bg-green-500' : 'bg-red-500'}>
                    {paypalSettings.has_credentials ? 'Configured' : 'Not Configured'}
                  </Badge>
                </div>
              </div>
            )}

            <form onSubmit={savePaypalSettings} className="space-y-4">
              <div>
                <Label className="mb-2 block">Payment Mode</Label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={paypalForm.mode === 'sandbox' ? 'default' : 'outline'}
                    onClick={() => setPaypalForm({ ...paypalForm, mode: 'sandbox' })}
                    className="flex-1"
                  >
                    <ToggleLeft className="h-4 w-4 mr-2" />
                    Sandbox (Testing)
                  </Button>
                  <Button
                    type="button"
                    variant={paypalForm.mode === 'live' ? 'default' : 'outline'}
                    onClick={() => setPaypalForm({ ...paypalForm, mode: 'live' })}
                    className={`flex-1 ${paypalForm.mode === 'live' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                  >
                    <ToggleRight className="h-4 w-4 mr-2" />
                    Live (Production)
                  </Button>
                </div>
              </div>

              {paypalForm.mode === 'live' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-red-800 font-semibold">Warning: Live Mode</p>
                      <p className="text-red-600 text-sm">
                        Live mode processes real payments. Ensure your PayPal Business account is properly configured.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="paypal_client_id">Client ID</Label>
                  <Input
                    id="paypal_client_id"
                    value={paypalForm.client_id}
                    onChange={(e) => setPaypalForm({ ...paypalForm, client_id: e.target.value })}
                    placeholder="PayPal Client ID"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Current: {paypalSettings?.client_id || 'Not set'}
                  </p>
                </div>
                <div>
                  <Label htmlFor="paypal_secret">Secret (leave blank to keep current)</Label>
                  <Input
                    id="paypal_secret"
                    type="password"
                    value={paypalForm.secret}
                    onChange={(e) => setPaypalForm({ ...paypalForm, secret: e.target.value })}
                    placeholder="PayPal Secret"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Current: {paypalSettings?.secret_masked || 'Not set'}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="submit" disabled={paypalSaving}>
                  {paypalSaving ? 'Saving...' : 'Save Settings'}
                </Button>
                <Button type="button" variant="outline" onClick={testPaypalConnection}>
                  <TestTube className="h-4 w-4 mr-2" />
                  Test Connection
                </Button>
              </div>

              {paypalTestResult && (
                <div className={`p-4 rounded-lg ${paypalTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    {paypalTestResult.success ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                    <p className={paypalTestResult.success ? 'text-green-800' : 'text-red-800'}>
                      {paypalTestResult.message}
                    </p>
                  </div>
                </div>
              )}
            </form>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-2">Where to get PayPal credentials?</h4>
              <ol className="text-sm text-slate-600 list-decimal list-inside space-y-1">
                <li>Go to <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">PayPal Developer Dashboard</a></li>
                <li>Create or select an app under "REST API apps"</li>
                <li>Copy the Client ID and Secret</li>
                <li>For Sandbox, use Sandbox credentials; for Live, use Live credentials</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        {/* Site Settings Section (Home Page Videos - Multilingual) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-primary" />
              Home Page Introduction Videos
            </CardTitle>
            <CardDescription>
              Add YouTube video URLs for the home page in different languages. Users will see the video in their selected language. If no video is set for their language, the English video is shown.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={updateSiteSettings} className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Label className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <span className="text-lg">🇺🇸</span> English Video (Default)
                </Label>
                <Input
                  type="url"
                  value={siteSettingsForm.intro_video_url || ''}
                  onChange={(e) => setSiteSettingsForm(prev => ({ ...prev, intro_video_url: e.target.value }))}
                  placeholder="https://www.youtube.com/watch?v=VIDEO_ID"
                  className="mt-1"
                />
                {siteSettingsForm.intro_video_url && (
                  <p className="text-xs text-green-600 mt-1">✓ English video configured</p>
                )}
              </div>

              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <Label className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <span className="text-lg">🇫🇷</span> French Video (Optional)
                </Label>
                <Input
                  type="url"
                  value={siteSettingsForm.intro_video_url_fr || ''}
                  onChange={(e) => setSiteSettingsForm(prev => ({ ...prev, intro_video_url_fr: e.target.value }))}
                  placeholder="https://www.youtube.com/watch?v=FRENCH_VIDEO_ID"
                  className="mt-1"
                />
                {siteSettingsForm.intro_video_url_fr && (
                  <p className="text-xs text-green-600 mt-1">✓ French video configured</p>
                )}
              </div>

              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <Label className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <span className="text-lg">🇪🇸</span> Spanish Video (Optional)
                </Label>
                <Input
                  type="url"
                  value={siteSettingsForm.intro_video_url_es || ''}
                  onChange={(e) => setSiteSettingsForm(prev => ({ ...prev, intro_video_url_es: e.target.value }))}
                  placeholder="https://www.youtube.com/watch?v=SPANISH_VIDEO_ID"
                  className="mt-1"
                />
                {siteSettingsForm.intro_video_url_es && (
                  <p className="text-xs text-green-600 mt-1">✓ Spanish video configured</p>
                )}
              </div>

              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <Label className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <span className="text-lg">🇩🇪</span> German Video (Optional)
                </Label>
                <Input
                  type="url"
                  value={siteSettingsForm.intro_video_url_de || ''}
                  onChange={(e) => setSiteSettingsForm(prev => ({ ...prev, intro_video_url_de: e.target.value }))}
                  placeholder="https://www.youtube.com/watch?v=GERMAN_VIDEO_ID"
                  className="mt-1"
                />
                {siteSettingsForm.intro_video_url_de && (
                  <p className="text-xs text-green-600 mt-1">✓ German video configured</p>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Tip:</strong> Using YouTube URLs is recommended - no storage costs, and videos load faster from YouTube's global CDN.
                </p>
              </div>

              <Button type="submit" disabled={siteSettingsSaving}>
                {siteSettingsSaving ? 'Saving...' : 'Save Video Settings'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Exchange Rate Settings Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Live Exchange Rate
            </CardTitle>
            <CardDescription>
              Exchange rates are fetched automatically from the European Central Bank via frankfurter.app. Rates update every hour.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Automated:</strong> When users select EUR, prices are converted from USD using the live exchange rate. 
                PayPal handles the final currency conversion and deposits USD to your business account.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-500">Current Rate</p>
                <p className="text-2xl font-bold text-slate-900">1 USD = {exchangeRate.eur_rate} EUR</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-500">Preview</p>
                <p className="text-2xl font-bold text-slate-900">$100 = \u20ac{(100 * parseFloat(exchangeRate.eur_rate || 0.92)).toFixed(2)}</p>
              </div>
            </div>
            
            {exchangeRate.updated_at && (
              <p className="text-xs text-slate-500">
                Last updated: {new Date(exchangeRate.updated_at).toLocaleString()} (Source: Live ECB rates)
              </p>
            )}
            
            <Button 
              variant="outline" 
              disabled={exchangeRateSaving}
              onClick={async () => {
                setExchangeRateSaving(true);
                try {
                  const res = await axios.post(`${API}/admin/settings/exchange-rate`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                  setExchangeRate({ eur_rate: res.data.eur_rate, updated_at: new Date().toISOString() });
                  toast.success(res.data.message);
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to refresh rate');
                } finally {
                  setExchangeRateSaving(false);
                }
              }}
              data-testid="refresh-exchange-rate-btn"
            >
              {exchangeRateSaving ? 'Refreshing...' : 'Refresh Rate Now'}
            </Button>
          </CardContent>
        </Card>

        {/* SEO Sitemap Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              SEO Sitemap Management
            </CardTitle>
            <CardDescription>
              Manage your sitemap for search engine optimization. The sitemap auto-regenerates when events or exhibitors are added.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {sitemapStatus && (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-600">Sitemap Status</p>
                  <p className="text-lg font-semibold flex items-center gap-2">
                    {sitemapStatus.sitemap_exists ? (
                      <>
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span className="text-green-700">Active</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-5 w-5 text-red-500" />
                        <span className="text-red-700">Not Found</span>
                      </>
                    )}
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-600">Last Regenerated</p>
                  <p className="text-lg font-semibold">
                    {sitemapStatus.last_modified 
                      ? new Date(sitemapStatus.last_modified).toLocaleString()
                      : 'Never'}
                  </p>
                </div>
              </div>
            )}

            {sitemapStatus?.current_counts && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 font-medium mb-2">Current Content Indexed:</p>
                <div className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    <span className="text-blue-800">{sitemapStatus.current_counts.events} Events</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Store className="h-4 w-4 text-blue-600" />
                    <span className="text-blue-800">{sitemapStatus.current_counts.exhibitors} Exhibitors</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-blue-600" />
                    <span className="text-blue-800">9 Static Pages</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button 
                onClick={regenerateSitemap} 
                disabled={sitemapRegenerating}
                className="flex items-center gap-2"
              >
                {sitemapRegenerating ? (
                  <>
                    <span className="animate-spin">\u27f3</span>
                    Regenerating...
                  </>
                ) : (
                  <>
                    <Globe className="h-4 w-4" />
                    Regenerate Sitemap
                  </>
                )}
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.open('https://tradehub24.co/sitemap.xml', '_blank')}
              >
                View Sitemap
              </Button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> The sitemap automatically updates when new events or exhibitors are added. 
                Use manual regeneration if you notice the sitemap is out of sync with your content.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Database Statistics Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-primary" />
              Database Statistics
            </CardTitle>
            <CardDescription>
              Overview of all data stored in the system.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {dbStats ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-slate-900">{dbStats.events}</p>
                  <p className="text-sm text-slate-600">Events</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-slate-900">{dbStats.booths}</p>
                  <p className="text-sm text-slate-600">Booths</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-slate-900">{dbStats.users?.total || 0}</p>
                  <p className="text-sm text-slate-600">Total Users</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-slate-900">{dbStats.registrations}</p>
                  <p className="text-sm text-slate-600">Registrations</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-lg font-bold text-blue-900">{dbStats.users?.admins || 0}</p>
                  <p className="text-sm text-blue-600">Admins</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-lg font-bold text-green-900">{dbStats.users?.vendors || 0}</p>
                  <p className="text-sm text-green-600">Vendors</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-lg font-bold text-purple-900">{dbStats.users?.attendees || 0}</p>
                  <p className="text-sm text-purple-600">Attendees</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <p className="text-lg font-bold text-orange-900">{dbStats.users?.reps || 0}</p>
                  <p className="text-sm text-orange-600">Reps</p>
                </div>
              </div>
            ) : (
              <p className="text-slate-500">Loading statistics...</p>
            )}
          </CardContent>
        </Card>

        {/* Tutorial Downloads Section */}
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Download className="h-5 w-5" />
              Tutorial Documents
            </CardTitle>
            <CardDescription className="text-blue-600">
              Download user guides for admins, attendees, and exhibitors.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <a 
                href={`${API}/docs/download/TradeHub24_Admin_Guide.docx`}
                className="flex items-center justify-center gap-2 px-4 py-3 border border-blue-300 rounded-lg hover:bg-blue-100 transition-colors text-blue-700 font-medium"
                download
              >
                <Shield className="h-4 w-4" />
                Admin Guide
              </a>
              <a 
                href={`${API}/docs/download/TradeHub24_Attendee_Guide.docx`}
                className="flex items-center justify-center gap-2 px-4 py-3 border border-blue-300 rounded-lg hover:bg-blue-100 transition-colors text-blue-700 font-medium"
                download
              >
                <Users className="h-4 w-4" />
                Attendee Guide
              </a>
              <a 
                href={`${API}/docs/download/TradeHub24_Exhibitor_Guide.docx`}
                className="flex items-center justify-center gap-2 px-4 py-3 border border-blue-300 rounded-lg hover:bg-blue-100 transition-colors text-blue-700 font-medium"
                download
              >
                <Store className="h-4 w-4" />
                Exhibitor Guide
              </a>
            </div>
            <p className="text-xs text-blue-600 mt-3">
              Click to download .docx files. Share these with your users to help them get started.
            </p>
          </CardContent>
        </Card>

        {/* Quick Cleanup Section */}
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <Trash2 className="h-5 w-5" />
              Quick Cleanup
            </CardTitle>
            <CardDescription className="text-amber-600">
              Clear specific data types without a full database reset.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Button 
                variant="outline" 
                className="border-amber-300 hover:bg-amber-100"
                onClick={async () => {
                  if (window.confirm('Clear all video call records (interactions)?')) {
                    try {
                      await axios.post(`${API}/admin/database/clear-collection`, { collection: 'interactions' });
                      toast.success('Video call records cleared');
                      fetchAdminData();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to clear');
                    }
                  }
                }}
              >
                <Video className="h-4 w-4 mr-2" />
                Clear Video Calls
              </Button>
              <Button 
                variant="outline" 
                className="border-amber-300 hover:bg-amber-100"
                onClick={async () => {
                  if (window.confirm('Clear all messages and conversations?')) {
                    try {
                      await axios.post(`${API}/admin/database/clear-collection`, { collection: 'messages' });
                      await axios.post(`${API}/admin/database/clear-collection`, { collection: 'conversations' });
                      toast.success('Messages cleared');
                      fetchAdminData();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to clear');
                    }
                  }
                }}
              >
                <Mail className="h-4 w-4 mr-2" />
                Clear Messages
              </Button>
              <Button 
                variant="outline" 
                className="border-amber-300 hover:bg-amber-100"
                onClick={async () => {
                  if (window.confirm('Delete all representative accounts?')) {
                    try {
                      await axios.post(`${API}/admin/database/clear-collection`, { collection: 'reps' });
                      toast.success('Representatives cleared');
                      fetchAdminData();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to clear');
                    }
                  }
                }}
              >
                <Users className="h-4 w-4 mr-2" />
                Clear Reps
              </Button>
              <Button 
                variant="outline" 
                className="border-amber-300 hover:bg-amber-100"
                onClick={async () => {
                  if (window.confirm('Clear booth visitor records?')) {
                    try {
                      await axios.post(`${API}/admin/database/clear-collection`, { collection: 'booth_visitors' });
                      toast.success('Booth visitors cleared');
                      fetchAdminData();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to clear');
                    }
                  }
                }}
              >
                <Eye className="h-4 w-4 mr-2" />
                Clear Visitors
              </Button>
            </div>
            <p className="text-xs text-amber-700 mt-3">
              Current counts: {dbStats?.interactions || 0} calls, {dbStats?.messages || 0} messages, {dbStats?.users?.reps || 0} reps
            </p>
          </CardContent>
        </Card>

        {/* File Health Check Section */}
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Image className="h-5 w-5" />
              {t('dashboard.file_health_check') || 'File Health Check'}
            </CardTitle>
            <CardDescription className="text-blue-600">
              {t('dashboard.file_health_description') || 'Check if all uploaded images and files exist on the server. Missing files may occur after deployment.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button 
                  onClick={checkFileHealth}
                  disabled={fileHealthLoading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {fileHealthLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      {t('common.checking') || 'Checking...'}
                    </>
                  ) : (
                    <>
                      <Database className="h-4 w-4 mr-2" />
                      {t('dashboard.run_file_check') || 'Run File Check'}
                    </>
                  )}
                </Button>
                
                {fileHealthCheck && (fileHealthCheck.total_missing > 0 || fileHealthCheck.total_external_urls > 0) && (
                  <Button 
                    onClick={cleanupTestData}
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Cleanup Test Data
                  </Button>
                )}
              </div>

              {fileHealthCheck && (
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg ${fileHealthCheck.status === 'ok' ? 'bg-green-100 border border-green-200' : 'bg-amber-100 border border-amber-200'}`}>
                    <div className="flex items-center gap-3">
                      {fileHealthCheck.status === 'ok' ? (
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      ) : (
                        <AlertTriangle className="h-6 w-6 text-amber-600" />
                      )}
                      <div>
                        <p className={`font-semibold ${fileHealthCheck.status === 'ok' ? 'text-green-800' : 'text-amber-800'}`}>
                          {fileHealthCheck.status === 'ok' 
                            ? (t('dashboard.all_files_present') || 'All Files Present') 
                            : (t('dashboard.missing_files_found') || 'Missing Files Found')}
                        </p>
                        <p className={`text-sm ${fileHealthCheck.status === 'ok' ? 'text-green-600' : 'text-amber-600'}`}>
                          {fileHealthCheck.total_missing} {t('dashboard.missing_local') || 'missing local files'} • {fileHealthCheck.total_external_urls} {t('dashboard.external_urls') || 'external URLs'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {fileHealthCheck.total_missing > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-slate-700">{t('dashboard.missing_files_details') || 'Missing Files Details'}</h4>
                      
                      {fileHealthCheck.missing_files.events.length > 0 && (
                        <div className="bg-white p-3 rounded border">
                          <p className="font-medium text-sm text-slate-600 mb-2">
                            {t('dashboard.events') || 'Events'} ({fileHealthCheck.missing_files.events.length})
                          </p>
                          <ul className="text-xs space-y-1 text-slate-500 max-h-32 overflow-y-auto">
                            {fileHealthCheck.missing_files.events.map((item, idx) => (
                              <li key={idx}>• {item.name}: <code className="bg-slate-100 px-1 rounded">{item.url}</code></li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {fileHealthCheck.missing_files.booths.length > 0 && (
                        <div className="bg-white p-3 rounded border">
                          <p className="font-medium text-sm text-slate-600 mb-2">
                            {t('dashboard.booths') || 'Booths'} ({fileHealthCheck.missing_files.booths.length})
                          </p>
                          <ul className="text-xs space-y-1 text-slate-500 max-h-32 overflow-y-auto">
                            {fileHealthCheck.missing_files.booths.map((item, idx) => (
                              <li key={idx}>• {item.company} ({item.field}): <code className="bg-slate-100 px-1 rounded">{item.url}</code></li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {fileHealthCheck.missing_files.products.length > 0 && (
                        <div className="bg-white p-3 rounded border">
                          <p className="font-medium text-sm text-slate-600 mb-2">
                            {t('dashboard.products') || 'Products'} ({fileHealthCheck.missing_files.products.length})
                          </p>
                          <ul className="text-xs space-y-1 text-slate-500 max-h-32 overflow-y-auto">
                            {fileHealthCheck.missing_files.products.map((item, idx) => (
                              <li key={idx}>• {item.name}: <code className="bg-slate-100 px-1 rounded">{item.url}</code></li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="bg-blue-50 border border-blue-200 rounded p-3">
                        <p className="text-sm text-blue-800">
                          <strong>{t('dashboard.fix_tip') || 'How to fix'}:</strong> These files weren't transferred during deployment. Re-upload through:
                        </p>
                        <ul className="text-sm text-blue-700 mt-2 ml-4 list-disc">
                          <li><strong>Events:</strong> Events tab → Edit event → Upload banner</li>
                          <li><strong>Booths:</strong> Exhibitors tab → Click edit icon on booth → Upload logo/banner/media</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {fileHealthCheck.total_external_urls > 0 && (
                    <div className="bg-slate-100 border border-slate-200 rounded p-3">
                      <p className="text-sm text-slate-700">
                        <strong>{t('dashboard.note') || 'Note'}:</strong> {fileHealthCheck.total_external_urls} {t('dashboard.external_url_note') || 'items use external URLs (e.g., example.com). These are typically test data and won\'t display actual images.'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Database Reset Section */}
        <Card className="border-red-200 bg-red-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Database Reset (Danger Zone)
            </CardTitle>
            <CardDescription className="text-red-600">
              Clear all test data before going live. This action is irreversible.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-red-100 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800 text-sm">
                <strong>Warning:</strong> This will permanently delete all events, booths, products, 
                registrations, messages, and uploaded files. Admin accounts can be preserved.
              </p>
            </div>
            <Button 
              variant="destructive" 
              onClick={() => setShowResetDbModal(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Reset Database
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Create Admin Modal */}
      <Dialog open={showCreateAdminModal} onOpenChange={setShowCreateAdminModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Admin</DialogTitle>
            <DialogDescription>
              Add a new administrator account. The new admin will have full access to manage the platform.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createAdmin} className="space-y-4">
            <div>
              <Label htmlFor="admin-name">Full Name *</Label>
              <Input
                id="admin-name"
                value={newAdminForm.name}
                onChange={(e) => setNewAdminForm({ ...newAdminForm, name: e.target.value })}
                required
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label htmlFor="admin-email">Email Address *</Label>
              <Input
                id="admin-email"
                type="email"
                value={newAdminForm.email}
                onChange={(e) => setNewAdminForm({ ...newAdminForm, email: e.target.value })}
                required
                placeholder="admin@company.com"
              />
            </div>
            <div>
              <Label htmlFor="admin-password">Password *</Label>
              <Input
                id="admin-password"
                type="password"
                value={newAdminForm.password}
                onChange={(e) => setNewAdminForm({ ...newAdminForm, password: e.target.value })}
                required
                minLength={6}
                placeholder="Minimum 6 characters"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateAdminModal(false)}>Cancel</Button>
              <Button type="submit">
                <UserPlus className="h-4 w-4 mr-2" />
                Create Admin
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Database Reset Modal */}
      <Dialog open={showResetDbModal} onOpenChange={setShowResetDbModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Reset Database
            </DialogTitle>
            <DialogDescription>
              This will permanently delete all data. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={resetDatabase} className="space-y-4">
            <div className="bg-red-100 border border-red-300 rounded-lg p-4 text-red-800 text-sm">
              <p className="font-semibold mb-2">\u26a0\ufe0f This will delete:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>All events and registrations</li>
                <li>All booths and products</li>
                <li>All messages and conversations</li>
                <li>All uploaded files (logos, banners, documents)</li>
                <li>All non-admin user accounts (if selected)</li>
              </ul>
            </div>
            
            <div>
              <Label htmlFor="reset-password">Your Password (for confirmation) *</Label>
              <Input
                id="reset-password"
                type="password"
                value={resetDbForm.confirmation_password}
                onChange={(e) => setResetDbForm({ ...resetDbForm, confirmation_password: e.target.value })}
                required
                placeholder="Enter your admin password"
              />
            </div>
            
            <div>
              <Label htmlFor="reset-key">Reset Key *</Label>
              <Input
                id="reset-key"
                type="password"
                value={resetDbForm.reset_key}
                onChange={(e) => setResetDbForm({ ...resetDbForm, reset_key: e.target.value })}
                required
                placeholder="Enter the database reset key"
              />
              <p className="text-xs text-slate-500 mt-1">
                Contact your system administrator for the reset key.
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="keep-admin"
                checked={resetDbForm.keep_admin}
                onChange={(e) => setResetDbForm({ ...resetDbForm, keep_admin: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label htmlFor="keep-admin" className="text-sm">
                Keep all admin accounts (recommended)
              </Label>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowResetDbModal(false)}>Cancel</Button>
              <Button type="submit" variant="destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Reset Database
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminSettingsTab;
